/*
  cart-confirm.js

  Confirmation gate for "Clear Cart" — the drawer button (#CartDrawer-ClearCart,
  handled in global.js) and the cart page button ([data-cr-removesel], handled in
  cart-redesign.js).

  Rather than editing both handlers, this listens on document in the CAPTURE
  phase, which runs before either of those bubble-phase listeners. The first
  click is swallowed and the dialog is shown; on confirm the same button is
  clicked again carrying a one-shot flag that lets it through untouched.
*/
(function () {
  'use strict';

  var TARGETS = '#CartDrawer-ClearCart, [data-cr-removesel]';
  var PASS = '__cartConfirmPass';
  var overlay = null;
  var pending = null;
  var lastFocus = null;

  /* ------------------------------------------------------------------ i18n */

  var FALLBACK = {
    title: 'Clear your cart?',
    body: 'All items will be removed from your cart. This cannot be undone.',
    yes: 'Yes, clear cart',
    no: 'Cancel'
  };

  var strings = null;

  function t() {
    if (strings) return strings;
    var src = document.querySelector('[data-cconf-i18n]');
    strings = {
      title: (src && src.getAttribute('data-title')) || FALLBACK.title,
      body: (src && src.getAttribute('data-body')) || FALLBACK.body,
      yes: (src && src.getAttribute('data-yes')) || FALLBACK.yes,
      no: (src && src.getAttribute('data-no')) || FALLBACK.no
    };
    return strings;
  }

  /* ------------------------------------------------------------------ view */

  function build() {
    if (overlay) return overlay;
    var s = t();

    overlay = document.createElement('div');
    overlay.className = 'cconf';
    overlay.setAttribute('hidden', '');
    overlay.innerHTML =
      '<div class="cconf__box" role="alertdialog" aria-modal="true" aria-labelledby="CConfTitle" aria-describedby="CConfBody">' +
        '<span class="cconf__icon" aria-hidden="true">' +
          '<svg viewBox="0 0 24 24" fill="none">' +
            '<polyline points="3 6 5 6 21 6" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>' +
            '<path d="M19 6l-1 14H6L5 6" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>' +
            '<path d="M10 11v6M14 11v6" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>' +
            '<path d="M9 6V4h6v2" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>' +
          '</svg>' +
        '</span>' +
        '<p class="cconf__title" id="CConfTitle"></p>' +
        '<p class="cconf__body" id="CConfBody"></p>' +
        '<div class="cconf__actions">' +
          '<button type="button" class="cconf__btn cconf__btn--no" data-cconf-no></button>' +
          '<button type="button" class="cconf__btn cconf__btn--yes" data-cconf-yes></button>' +
        '</div>' +
      '</div>';

    overlay.querySelector('.cconf__title').textContent = s.title;
    overlay.querySelector('.cconf__body').textContent = s.body;
    overlay.querySelector('[data-cconf-no]').textContent = s.no;
    overlay.querySelector('[data-cconf-yes]').textContent = s.yes;

    overlay.addEventListener('click', function (e) {
      if (e.target === overlay) close(false);
      else if (e.target.closest('[data-cconf-no]')) close(false);
      else if (e.target.closest('[data-cconf-yes]')) close(true);
    });

    document.body.appendChild(overlay);
    return overlay;
  }

  function open(btn) {
    pending = btn;
    lastFocus = document.activeElement;
    var el = build();
    el.hidden = false;
    /* Next frame so the transition has a starting state to animate from. */
    requestAnimationFrame(function () { el.classList.add('is--on'); });
    var yes = el.querySelector('[data-cconf-yes]');
    if (yes) setTimeout(function () { yes.focus(); }, 60);
  }

  function close(go) {
    if (!overlay) return;
    overlay.classList.remove('is--on');
    var btn = pending;
    pending = null;

    setTimeout(function () { if (overlay) overlay.hidden = true; }, 180);

    if (go && btn) {
      btn[PASS] = true;
      btn.click();
    } else if (lastFocus && lastFocus.focus) {
      try { lastFocus.focus(); } catch (err) {}
    }
  }

  /* --------------------------------------------------------------- capture */

  document.addEventListener(
    'click',
    function (e) {
      if (!e.target.closest) return;
      var btn = e.target.closest(TARGETS);
      if (!btn) return;

      /* Second pass — let the real handler run. */
      if (btn[PASS]) {
        delete btn[PASS];
        return;
      }

      /* Cart page with no rows left: the handler bails anyway, so don't ask. */
      var page = document.querySelector('[data-cr-items]');
      if (btn.hasAttribute('data-cr-removesel') && page && !page.querySelector('[data-cr-line]')) return;

      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      open(btn);
    },
    true
  );

  document.addEventListener('keydown', function (e) {
    if (!overlay || overlay.hidden) return;
    if (e.key === 'Escape') { e.preventDefault(); close(false); }
  });

  /* --------------------------------------------------------------- styles */

  var css =
    /* Above cart-drawer, which sits at 10000000. */
    '.cconf{position:fixed;inset:0;z-index:2147483000;display:flex;align-items:center;justify-content:center;padding:20px;' +
    'background:rgba(0,0,0,.45);opacity:0;transition:opacity .18s ease}' +
    '.cconf[hidden]{display:none}' +
    '.cconf.is--on{opacity:1}' +
    '.cconf__box{box-sizing:border-box;width:100%;max-width:360px;padding:24px 22px 20px;border-radius:16px;background:#fff;' +
    'text-align:center;box-shadow:0 18px 44px rgba(0,0,0,.18);transform:translateY(10px) scale(.97);transition:transform .18s ease}' +
    '.cconf.is--on .cconf__box{transform:none}' +
    '.cconf__icon{display:inline-flex;align-items:center;justify-content:center;width:52px;height:52px;margin-bottom:14px;' +
    'border-radius:50%;background:#f2f2f2;color:#121212}' +
    '.cconf__icon svg{width:24px;height:24px}' +
    '.cconf__title{margin:0 0 8px;font-size:18px;font-weight:700;line-height:1.3;color:#121212}' +
    '.cconf__body{margin:0;font-size:14px;line-height:1.45;color:rgba(18,18,18,.62)}' +
    '.cconf__actions{display:flex;gap:10px;margin-top:20px}' +
    '.cconf__btn{flex:1 1 0;min-height:46px;padding:12px 14px;border-radius:10px;font-family:inherit;font-size:14px;' +
    'font-weight:600;line-height:1;cursor:pointer}' +
    '.cconf__btn--no{border:1px solid rgba(18,18,18,.22);background:#fff;color:#121212}' +
    '.cconf__btn--no:hover{background:#f6f6f6}' +
    '.cconf__btn--yes{border:1px solid #121212;background:#121212;color:#fff}' +
    '.cconf__btn--yes:hover{filter:brightness(1.35)}' +
    '@media screen and (max-width:420px){.cconf__box{padding:20px 18px 18px}.cconf__title{font-size:17px}}';

  var tag = document.createElement('style');
  tag.textContent = css;
  document.head.appendChild(tag);
})();
